import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import useMagicLinkVerification from '../../access/useMagicLinkVerification';
import { handleHandoffEnvelope } from './handleHandoffEnvelope';

const AUTO_SAVE_DELAY_MS = 1000;

function getLocalDraftKey(resourceId) {
  return `w86_form_draft_${resourceId}`;
}

function readLocalDraft(resourceId) {
  try {
    const raw = localStorage.getItem(getLocalDraftKey(resourceId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.answers === 'object' && parsed.savedAt) return parsed;
  } catch { /* corrupt or unavailable */ }
  return null;
}

function writeLocalDraft(resourceId, answers) {
  try {
    localStorage.setItem(
      getLocalDraftKey(resourceId),
      JSON.stringify({ answers, savedAt: Date.now() })
    );
  } catch { /* quota exceeded or unavailable */ }
}

function clearLocalDraft(resourceId) {
  try {
    localStorage.removeItem(getLocalDraftKey(resourceId));
  } catch { /* ignore */ }
}

function clientValidate(fields, answers) {
  if (!Array.isArray(fields)) return {};
  const errors = {};
  for (const field of fields) {
    if (field.type === 'display') continue;
    if (!field.required) continue;
    const val = answers?.[field.key];
    const empty =
      val === undefined ||
      val === null ||
      val === '' ||
      (Array.isArray(val) && val.length === 0);
    if (empty) {
      errors[field.key] = 'This field is required';
    }
  }
  return errors;
}

const STATUS_ERROR_MESSAGES = {
  403: 'You do not have permission to access this form.',
  404: 'This form link is invalid or has been revoked.',
  410: 'This form link has expired.',
};

function resolveErrorMessage(error, fallback) {
  const status = error?.response?.status;
  if (STATUS_ERROR_MESSAGES[status]) return STATUS_ERROR_MESSAGES[status];
  const data = error?.response?.data;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      if (parsed?.error) return parsed.error;
    } catch { /* not JSON */ }
    return data;
  }
  if (data?.error) return data.error;
  if (data?.message) return data.message;
  return error?.message || fallback;
}

export default function usePublicForm({ adapter, resourceId, onInstanceCreated }) {
  const verifyMagicLink = useCallback(
    async (verificationToken) => adapter.verifyMagicLink(verificationToken),
    [adapter]
  );

  const { grantToken, verificationError, clearGrantToken } = useMagicLinkVerification(
    resourceId, verifyMagicLink
  );

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFieldKey, setUploadingFieldKey] = useState(null);
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [serverValidation, setServerValidation] = useState({ errors: {}, warnings: {} });
  const [error, setError] = useState('');
  const [gateState, setGateState] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});
  const [showAllValidation, setShowAllValidation] = useState(false);
  const [authPassed, setAuthPassed] = useState(false);

  const answersRef = useRef({});
  const blurValidationInFlightRef = useRef(new Set());
  const lastValidatedFieldValueRef = useRef({});
  const latestBlurRequestIdRef = useRef(0);
  const autoSaveTimerRef = useRef(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'restored'
  const instanceNotifiedRef = useRef(false);

  useEffect(() => {
    if (verificationError) setError(verificationError);
  }, [verificationError]);

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  // Bootstrap + gate state
  useEffect(() => {
    if (!resourceId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const result = await adapter.bootstrap(grantToken);
        if (cancelled) return;

        if (result.notPublished) {
          setGateState('not_published');
          setLoading(false);
          return;
        }
        if (result.disabled) {
          setGateState('disabled');
          setLoading(false);
          return;
        }
        if (result.requiresEmailVerification && !grantToken) {
          setGateState('email_required');
          setLoading(false);
          return;
        }
        if (result.requiresAuth && !authPassed) {
          setGateState('auth_required');
          setLoading(false);
          return;
        }

        // Gate passed, load form
        setGateState(null);
        await loadForm(cancelled);
      } catch (e) {
        if (cancelled) return;
        await loadForm(cancelled);
      }
    })();
    return () => { cancelled = true; };
  }, [resourceId, grantToken, authPassed]);

  const loadForm = async (cancelled) => {
    try {
      const data = await adapter.loadForm(grantToken);
      if (cancelled) return;
      setForm(data);
      setAnswers(data?.answers || {});
      answersRef.current = data?.answers || {};
      lastValidatedFieldValueRef.current = {};
      setTouchedFields({});
      setShowAllValidation(false);
      setError('');
      setGateState(null);

      // Restore localStorage draft if present and server answers are empty
      const localDraft = readLocalDraft(resourceId);
      if (localDraft && localDraft.answers && Object.keys(localDraft.answers).length > 0) {
        const serverAnswers = data?.answers || {};
        const hasServerAnswers = Object.keys(serverAnswers).some(
          (k) => serverAnswers[k] !== undefined && serverAnswers[k] !== null && serverAnswers[k] !== ''
        );
        if (!hasServerAnswers) {
          setAnswers(localDraft.answers);
          answersRef.current = localDraft.answers;
          setAutoSaveStatus('restored');
          setTimeout(() => setAutoSaveStatus(null), 3000);
        }
      }
    } catch (e) {
      if (cancelled) return;
      if (e?.response?.status === 401) {
        setGateState('auth_required');
        setError('');
      } else if (e?.response?.status === 403 && grantToken) {
        clearGrantToken();
        setGateState('email_required');
        setError('');
      } else {
        setError(resolveErrorMessage(e, 'Unable to load form.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const onChangeAnswer = useCallback((fieldKey, nextValue) => {
    setAnswers((prev) => {
      const next = { ...prev, [fieldKey]: nextValue };
      answersRef.current = next;
      return next;
    });
    // Debounced localStorage auto-save
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    setAutoSaveStatus('saving');
    autoSaveTimerRef.current = setTimeout(() => {
      writeLocalDraft(resourceId, answersRef.current);
      setAutoSaveStatus('saved');
    }, AUTO_SAVE_DELAY_MS);
  }, [resourceId]);

  const markFieldTouched = useCallback((fieldKey) => {
    setTouchedFields((prev) => {
      if (prev[fieldKey]) return prev;
      return { ...prev, [fieldKey]: true };
    });
  }, []);

  const clearFieldValidation = useCallback((fieldKey) => {
    setServerValidation((prev) => {
      const errors = { ...prev.errors };
      const warnings = { ...prev.warnings };
      delete errors[fieldKey];
      delete warnings[fieldKey];
      return { errors, warnings };
    });
  }, []);

  // Server-side validation on blur
  const onFieldBlur = useCallback(async (fieldKey, nextValue) => {
    markFieldTouched(fieldKey);

    if (!adapter.capabilities.hasServerValidation || submitting) return;

    const serializedValue = JSON.stringify(nextValue ?? null);
    if (lastValidatedFieldValueRef.current[fieldKey] === serializedValue) return;
    if (blurValidationInFlightRef.current.has(fieldKey)) return;

    clearFieldValidation(fieldKey);

    blurValidationInFlightRef.current.add(fieldKey);
    const requestId = ++latestBlurRequestIdRef.current;
    const nextAnswers = { ...answersRef.current, [fieldKey]: nextValue };
    answersRef.current = nextAnswers;
    setAnswers(nextAnswers);

    try {
      const data = await adapter.validate(nextAnswers, grantToken);
      if (requestId !== latestBlurRequestIdRef.current) return;

      if (data?.validation) setServerValidation(data.validation);

      if (data?.form) {
        const serverAnswers = data.form.answers || nextAnswers;
        const localAnswers = { ...answersRef.current };
        const merged = { ...serverAnswers };
        for (const k of Object.keys(localAnswers)) {
          if (JSON.stringify(localAnswers[k]) !== JSON.stringify(nextAnswers[k])) {
            merged[k] = localAnswers[k];
          }
        }
        setForm((prev) => ({ ...(prev || {}), ...data.form, answers: merged }));
        answersRef.current = merged;
        setAnswers(merged);
        setError('');
      } else if (data?.status === 'VALIDATION_FAILED') {
        setError('Please fix the highlighted fields and try again.');
      } else {
        setError('');
      }

      lastValidatedFieldValueRef.current[fieldKey] = serializedValue;
    } catch (e) {
      if (requestId === latestBlurRequestIdRef.current) {
        setError(resolveErrorMessage(e, 'Unable to validate this field right now.'));
      }
    } finally {
      blurValidationInFlightRef.current.delete(fieldKey);
    }
  }, [adapter, grantToken, submitting, markFieldTouched, clearFieldValidation]);

  // File upload
  const onUpload = useCallback(async (fieldKey, file) => {
    if (!adapter.capabilities.canUpload) return;
    setUploadingFieldKey(fieldKey);
    try {
      const descriptor = await adapter.upload(fieldKey, file, grantToken);
      if (descriptor) {
        setAnswers((prev) => {
          const current = Array.isArray(prev[fieldKey]) ? prev[fieldKey] : [];
          const next = { ...prev, [fieldKey]: [...current, descriptor] };
          answersRef.current = next;
          return next;
        });
      }
    } catch (e) {
      setError(resolveErrorMessage(e, 'Upload failed.'));
    } finally {
      setUploadingFieldKey(null);
    }
  }, [adapter, grantToken]);

  // Save draft
  const saveDraft = useCallback(async () => {
    if (!adapter.capabilities.canSaveDraft) return;
    setSubmitting(true);
    try {
      const data = await adapter.saveDraft(answers, grantToken);
      if (data?.validation) setServerValidation(data.validation);
      if (data?.form) {
        const serverAnswers = data.form.answers || answers;
        const localAnswers = { ...answersRef.current };
        const sentAnswers = answers;
        const merged = { ...serverAnswers };
        for (const k of Object.keys(localAnswers)) {
          if (JSON.stringify(localAnswers[k]) !== JSON.stringify(sentAnswers[k])) {
            merged[k] = localAnswers[k];
          }
        }
        setForm((prev) => ({ ...(prev || {}), ...data.form, answers: merged }));
        setAnswers(merged);
        answersRef.current = merged;
      } else if (data?.status === 'VALIDATION_FAILED') {
        setError('Please fix the highlighted fields and try again.');
      }
      if (data?.form || data?.status !== 'VALIDATION_FAILED') {
        setError('');
        clearLocalDraft(resourceId);
        setAutoSaveStatus(null);
      }

      // Notify page of new instance so URL can be updated
      if (data?.instanceId && onInstanceCreated && !instanceNotifiedRef.current) {
        instanceNotifiedRef.current = true;
        onInstanceCreated(data.instanceId);
      }
    } catch (e) {
      setError(resolveErrorMessage(e, 'Failed to save draft.'));
    } finally {
      setSubmitting(false);
    }
  }, [adapter, answers, grantToken, resourceId, onInstanceCreated]);

  // Submit
  const submit = useCallback(async () => {
    setShowAllValidation(true);

    // Use the ref as the single source of truth for the answers payload. React
    // state (`answers`) can lag behind the latest keystroke if the user types
    // and clicks submit in quick succession. `answersRef.current` is updated
    // synchronously inside `onChangeAnswer`, so it always reflects the last
    // typed value. Validating, pre-saving, and submitting all use the same
    // snapshot to avoid inconsistent server state.
    const latestAnswers = answersRef.current;

    const clientErrors = clientValidate(form?.fields, latestAnswers);
    if (Object.keys(clientErrors).length > 0) return;

    setSubmitting(true);
    try {
      // Flush the latest answers to the server BEFORE submitting. This protects
      // against network failures in the submit call — if /submit errors out, the
      // user's input is already persisted against the form instance, so a page
      // reload re-hydrates their values instead of showing a blank form.
      // Only runs in modes that support drafts (FILL_REEDIT); the adapter returns
      // null otherwise. Ignore validation errors here — they will be surfaced by
      // the actual submit call.
      if (adapter.capabilities.canSaveDraft) {
        try {
          const preSave = await adapter.saveDraft(latestAnswers, grantToken);
          if (
            preSave?.instanceId &&
            onInstanceCreated &&
            !instanceNotifiedRef.current
          ) {
            instanceNotifiedRef.current = true;
            onInstanceCreated(preSave.instanceId);
          }
        } catch {
          // Non-fatal — backend /submit also persists answers. Proceed.
        }
      }

      const data = await adapter.submit(latestAnswers, grantToken);
      if (data?.validation) setServerValidation(data.validation);

      // Notify page of new instance so URL can be updated to
      // /dynamic-form/:componentId/:instanceId — this makes the submitted state
      // addressable (refresh / share-back) instead of lost after submit.
      if (data?.instanceId && onInstanceCreated && !instanceNotifiedRef.current) {
        instanceNotifiedRef.current = true;
        onInstanceCreated(data.instanceId);
      }

      // Phase 6: if the submit triggered a cross-component handoff, navigate and
      // short-circuit. Validation errors take precedence over handoff because a 4xx
      // response already doesn't carry a handoff envelope.
      if (data?.status !== 'VALIDATION_FAILED' && handleHandoffEnvelope(data)) {
        clearLocalDraft(resourceId);
        return;
      }

      if (data?.status === 'VALIDATION_FAILED') {
        setError('Please fix the highlighted fields and try again.');
      } else if (adapter.capabilities.showConfirmationOnSubmit) {
        setSubmitted(true);
        setError('');
        clearLocalDraft(resourceId);
        setAutoSaveStatus(null);
      } else if (data?.form) {
        const serverAnswers = data.form.answers || latestAnswers;
        // Re-read the ref now so we can rescue any keystrokes that landed
        // while the submit was in flight (between sending `latestAnswers`
        // and receiving `data`).
        const localAnswers = { ...answersRef.current };
        const merged = { ...serverAnswers };
        for (const k of Object.keys(localAnswers)) {
          if (JSON.stringify(localAnswers[k]) !== JSON.stringify(latestAnswers[k])) {
            merged[k] = localAnswers[k];
          }
        }
        // Preserve `submitted` flag from response (e.g. `data.form.submitted=true`)
        // so the button label can flip to "Resubmit" without a reload.
        setForm((prev) => ({ ...(prev || {}), ...data.form, answers: merged }));
        setAnswers(merged);
        answersRef.current = merged;
        setError('');
        clearLocalDraft(resourceId);
        setAutoSaveStatus(null);
      } else {
        setLoading(true);
        await loadForm(false);
        setError('');
        clearLocalDraft(resourceId);
        setAutoSaveStatus(null);
      }
    } catch (e) {
      setError(resolveErrorMessage(e, 'Failed to submit form.'));
    } finally {
      setSubmitting(false);
    }
  }, [adapter, form, grantToken, resourceId, onInstanceCreated]);

  // Refresh
  const refresh = useCallback(async () => {
    if (!adapter.capabilities.canRefresh || submitting) return;
    setLoading(true);
    await loadForm(false);
  }, [adapter, submitting, grantToken]);

  // Merged validation: client + server, filtered by touched state
  const clientErrors = useMemo(
    () => clientValidate(form?.fields, answers),
    [form?.fields, answers]
  );

  const filteredValidation = useMemo(() => {
    const mergedErrors = { ...clientErrors, ...(serverValidation.errors || {}) };
    const mergedWarnings = { ...(serverValidation.warnings || {}) };

    if (showAllValidation) return { errors: mergedErrors, warnings: mergedWarnings };

    const visibleKeys = new Set(Object.keys(touchedFields).filter((key) => touchedFields[key]));
    const filterBucket = (bucket = {}) =>
      Object.fromEntries(Object.entries(bucket).filter(([key]) => visibleKeys.has(key)));

    return {
      errors: filterBucket(mergedErrors),
      warnings: filterBucket(mergedWarnings),
    };
  }, [showAllValidation, touchedFields, clientErrors, serverValidation]);

  // Cleanup auto-save timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, []);

  const capabilities = useMemo(() => adapter.capabilities, [adapter, form]);

  const readOnly = adapter.deriveReadOnly(form);
  const canSubmit = adapter.deriveCanSubmit(form);
  const canEdit = adapter.deriveCanEdit(form);
  const coverImageUrl = form?.formSettings?.coverImageUrl || undefined;

  const requestMagicLink = useCallback(
    async (email) => adapter.requestMagicLink(email),
    [adapter]
  );

  const onSignedIn = useCallback(() => {
    setAuthPassed(true);
  }, []);

  return {
    loading,
    submitting,
    uploadingFieldKey,
    form,
    answers,
    validation: filteredValidation,
    error,
    gateState,
    submitted,
    readOnly,
    canSubmit,
    canEdit,
    coverImageUrl,
    capabilities,
    autoSaveStatus,
    onChangeAnswer,
    onFieldBlur,
    onUpload,
    saveDraft,
    submit,
    refresh,
    requestMagicLink,
    onSignedIn,
  };
}
